import React, { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import DataEditor, {
  GridCellKind,
  CompactSelection,
  GridColumnIcon,
} from "@glideapps/glide-data-grid";
import { Spinner, RadioGroup, Radio, Stack, Badge } from "@chakra-ui/react";
import "@glideapps/glide-data-grid/dist/index.css";
import { useLayer } from "react-laag";
import DataDetailsDrawer from "./DataDetailsDrawer";
import BadgeCellRenderer from "./BadgeCellRenderer";
import CustomModal from "./CustomModal";
// Helper function to map icon strings from config to actual icons
const mapIcon = (icon) => {
  switch (icon) {
    case "HeaderString":
      return GridColumnIcon.HeaderString;
    case "HeaderNumber":
      return GridColumnIcon.HeaderNumber;
    case "HeaderDate":
      return GridColumnIcon.HeaderCalendar;
    case "HeaderTime":
      return GridColumnIcon.HeaderTime;
    case "HeaderUser":
      return GridColumnIcon.HeaderUser;
    case "HeaderLocation":
      return GridColumnIcon.HeaderLocation;
    case "HeaderImage":
      return GridColumnIcon.HeaderImage;
    default:
      return GridColumnIcon.HeaderString; // Fallback icon
  }
};

const TableComponent = ({ data, config, tripsData, modalData, ...props }) => {
  const { columns: configColumns, styles, options } = config;

  // Memoize columns to avoid excessive re-rendering
  const cols = useMemo(
    () =>
      configColumns.map((col) => ({
        ...col,
        icon: mapIcon(col.icon), // Map string icon names to actual icons
        themeOverride: col.themeOverride || {}, // Apply column-specific theming if provided
      })),
    [configColumns]
  );
  // Track the columns to be positioned on the map
  const [columns, setColumns] = useState(cols);
  // State to manage table data, grid selection, and search functionality
  const [tableData, setTableData] = useState(data); // Initialize with data
  const [gridSelection, setGridSelection] = useState({
    current: undefined,
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty(),
  });
  const customRenderers = [BadgeCellRenderer(config)];
  const [showMenu, setShowMenu] = useState(null); // Menu visibility state
  const [highlightRegions, setHighlightRegions] = useState([]);
  const [showSearch, setShowSearch] = useState(false); // Search bar visibility state
  const [searchResults, setSearchResults] = useState([]); // State to store search results
  const [searchValue, setSearchValue] = useState("");
  const [selectedSearchResult, setSelectedSearchResult] = useState(null); // State to track a single selected search result
  const [navIndex, setNavIndex] = useState(0); // Track the current index in the search results
  const [lastUpdatedMap, setLastUpdatedMap] = useState({}); // Track the last updated map
  const [sortState, setSortState] = useState({ colId: null, order: null }); // Sorting state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModalData, setSelectedModalData] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  // Update the table data when the initial data changes
  useEffect(() => {
    setTableData(data);
  }, [data]);
  useEffect(() => {
    setColumns(cols);
  }, [cols]);
  const handleOpenModal = (data) => {
    setSelectedModalData({
      card1Content: data.card1Value,
      card2Content: data.card2Value,
    });
    setIsModalOpen(true);
  };
  // Handle column menu click to display the column menu
  const onHeaderMenuClick = useCallback((col, bounds) => {
    setShowMenu({ col, bounds });
  }, []);

  const handleCellClick = useCallback(
    ([col, row]) => {
      const column = columns[col];
      const cellValue = tableData[row]?.[column?.id]?.toString();
  
      // Ensure column and cellValue are defined before proceeding
      if (!column || !cellValue) return;
  
      if (column.triggerDrawer) {
        const tripData = tripsData[cellValue];
        if (tripData) {
          setSelectedTrip(tripData);
          setIsDrawerOpen(true);
        } else {
          console.error(`Data not found for ${column.id}: ${cellValue}`);
        }
      } else if (column.triggerModal) {
        const modalDataItem = modalData[cellValue];
        if (modalDataItem) {
          handleOpenModal(modalDataItem);
        } else {
          console.error(`Data not found for ${column.id}: ${cellValue}`);
          console.log("No matching modal data found.");
        }
      }
    },
    [columns, tableData, tripsData, modalData]
  );
  

  const { renderLayer, layerProps } = useLayer({
    isOpen: !!showMenu,
    onOutsideClick: () => setShowMenu(null),
    trigger: {
      getBounds: () => ({
        bottom: (showMenu?.bounds.y ?? 0) + (showMenu?.bounds.height ?? 0),
        height: showMenu?.bounds.height ?? 0,
        left: showMenu?.bounds.x ?? 0,
        right: (showMenu?.bounds.x ?? 0) + (showMenu?.bounds.width ?? 0),
        top: showMenu?.bounds.y ?? 0,
        width: showMenu?.bounds.width ?? 0,
      }),
    },
    placement: "bottom-start",
  });

  // Show search bar on Ctrl+F or Cmd+F
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "f") {
        event.preventDefault();
        setShowSearch(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Callback to retrieve the content of each cell, allowing editing
  const getCellContent = useCallback(
    ([col, row]) => {
      // Determine which data to display based on search results and navIndex
      const displayData = selectedSearchResult
        ? [selectedSearchResult]
        : searchResults.length > 0
          ? searchResults // Display all search results when searching
          : tableData;

      if (row >= displayData.length || col >= columns.length) {
        return { kind: GridCellKind.Text, displayData: "", data: "" };
      }

      const dataRow = displayData[row];
      const columnKey = columns[col].id;

      if (!columnKey || !dataRow) {
        return { kind: GridCellKind.Text, displayData: "", data: "" };
      }

      const cellValue = dataRow[columnKey];
      const cellKey = `${row}-${col}`;
      // Handle the case where columnKey or dataRow is undefined

      // Check if the column kind is "badge-cell" and return the correct cell kind
      if (columns[col].kind === "badge-cell") {
        return {
          kind: GridCellKind.Custom,
          allowOverlay: true,
          readonly: !columns[col].editable,
          data: {
            kind: "badge-cell",
            value: cellValue,
          },
        };
      }

      return {
        kind: GridCellKind.Text,
        allowOverlay: true, // Allow the overlay to appear for editing
        readonly: !columns[col].editable, // Set to true to make the cell non-editable
        displayData:
          cellValue !== null && cellValue !== undefined
            ? cellValue.toString()
            : "",
        data: cellValue,
        lastUpdated: lastUpdatedMap[cellKey] || 0, // Use last updated timestamp if available
      };
    },
    [
      tableData,
      searchResults,
      columns,
      selectedSearchResult,
      navIndex,
      lastUpdatedMap,
    ]
  );
  // Callback for handling cell edits
  const onCellEdited = useCallback(
    (cell, newValue) => {
      const [col, row] = cell;
      const columnKey = columns[col].id;
      const cellKey = `${row}-${col}`;

      // Correct the update logic to handle values properly
      setTableData((prevData) => {
        const updatedData = [...prevData];
        updatedData[row] = {
          ...updatedData[row],
          [columnKey]: newValue.data?.value || newValue.data, // Extract the value correctly
        };
        return updatedData;
      });

      setLastUpdatedMap((prevMap) => ({
        ...prevMap,
        [cellKey]: performance.now(), // Set the current timestamp for the updated cell
      }));
    },
    [columns, searchResults, tableData]
  );

  // Handle selection changes and update highlight regions
  const handleGridSelectionChange = useCallback(
    (newSelection) => {
      setGridSelection(newSelection);

      // Retrieve highlight colors from config
      const { current, column, rows } = options.highlightColors || {
        current: "rgba(0, 255, 0, 0.3)",
        column: "rgba(0, 0, 255, 0.3)",
        rows: "rgba(255, 0, 0, 0.3)",
      };

      // Generate new highlight regions based on the current selection
      const newHighlights = [];
      if (newSelection.current) {
        newHighlights.push({
          color: current, // Use the color from the config
          range: newSelection.current.range,
        });
      }

      // Add highlights for selected rows and columns
      newSelection.columns.toArray().forEach((col) => {
        newHighlights.push({
          color: column, // Use column highlight color
          range: { x: col, y: 0, width: 1, height: tableData.length },
        });
      });

      newSelection.rows.toArray().forEach((row) => {
        newHighlights.push({
          color: rows, // Use row highlight color
          range: { x: 0, y: row, width: columns.length, height: 1 },
        });
      });

      setHighlightRegions(newHighlights);
    },
    [columns.length, tableData.length, options.highlightColors]
  );

  // Handle search value change and update search results
  const handleSearchValueChange = useCallback(
    (newVal) => {
      setSearchValue(newVal);
      if (newVal) {
        const filteredResults = data
          .map((row, rowIndex) => {
            const matchedColumns = Object.entries(row)
              .map(([key, value]) => ({
                kind: GridCellKind.Text,
                data: value,
                displayData: value ? value.toString() : "",
                row: rowIndex,
                column: key,
              }))
              .filter((cell) =>
                cell.displayData.toLowerCase().includes(newVal.toLowerCase())
              );

            return matchedColumns.length > 0 ? row : null;
          })
          .filter(Boolean);

        setSearchResults(filteredResults);
        setNavIndex(0); // Reset the navigation index to the first item
      } else {
        setSearchResults([]);
        setSelectedSearchResult(null);
      }
    },
    [data]
  );

  // Handle search results change
  const handleSearchResultsChanged = useCallback((results, navIndex) => {
    setSearchResults(results);
    setSelectedSearchResult(results[navIndex] || null); // Update the selected search result based on navigation index
    setNavIndex(navIndex); // Update the navigation index
  }, []);

  // Handle search close
  const handleSearchClose = useCallback(() => {
    setShowSearch(false);
    setSearchValue("");
    setSearchResults([]);
    setSelectedSearchResult(null);
    setNavIndex(0);
  }, []);

  // Handle column resizing
  const onColumnResize = useCallback((columns, newSize, columnIndex) => {
    setColumns((prevColumns) => {
      const updatedColumns = [...prevColumns];
      updatedColumns[columnIndex] = { ...columns, width: newSize };
      return updatedColumns;
    });
  }, []);

  // Render the column menu when the header button is pressed
  const renderColumnMenu = () => {
    if (!showMenu) return null;

    // Handler to update the sorting state based on selected radio button
    const handleSortChange = (value) => {
      let order = value === "none" ? null : value; // Set to null if "none" is selected

      setSortState({ colId: columns[showMenu.col].id, order });

      if (!order) {
        // Reset to original data order if unsorted
        setTableData([...data]);
        return;
      }

      // Sort data based on selected column and order
      const sortedData = [...tableData].sort((a, b) => {
        const aValue = a[columns[showMenu.col].id];
        const bValue = b[columns[showMenu.col].id];

        // Use localeCompare for string comparison with case insensitivity and numeric sorting
        if (typeof aValue === "string" && typeof bValue === "string") {
          return order === "asc"
            ? aValue.localeCompare(bValue, undefined, {
                sensitivity: "base", // Case-insensitive and ignores accents
                numeric: true, // Numeric ordering
              })
            : bValue.localeCompare(aValue, undefined, {
                sensitivity: "base",
                numeric: true,
              });
        }

        // Default sorting for numbers and other types
        if (aValue < bValue) return order === "asc" ? -1 : 1;
        if (aValue > bValue) return order === "asc" ? 1 : -1;
        return 0;
      });

      setTableData(sortedData);
      setShowMenu(null);
    };

    return renderLayer(
      <div
        {...layerProps}
        style={{
          ...layerProps.style,
          position: "absolute",
          width: 180,
          padding: "8px",
          borderRadius: "8px",
          backgroundColor: props.filterDrawerBg,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          border: "1px solid #e2e8f0",
        }}
      >
        <RadioGroup
          value={
            sortState.colId === columns[showMenu.col].id
              ? sortState.order || "none"
              : "none"
          }
          onChange={handleSortChange}
        >
          <Stack direction="column">
            <Radio value="asc">Sort Ascending</Radio>
            <Radio value="desc">Sort Descending</Radio>
            <Radio value="none">None</Radio>
          </Stack>
        </RadioGroup>
      </div>
    );
  };

  // Calculate rows count based on search state
  const rowsCount = selectedSearchResult
    ? 1
    : searchResults.length > 0
      ? searchResults.length
      : tableData.length;

  // Display a spinner if no columns are defined
  if (columns.length === 0) {
    return <Spinner />;
  }

  // Render the DataEditor component with the required props, including selection handling
  return (
    <div
      style={{
        width: styles.width,
        height: styles.height,
        overflow: "auto",
        borderRadius: styles.borderRadius,
        boxShadow: styles.boxShadow,
        backgroundColor: styles.tableColor,
        border: `1px solid ${styles.borderColor}`,
      }}
    >
      <DataEditor
        getCellContent={getCellContent}
        onCellEdited={onCellEdited}
        onCellClicked={handleCellClick}
        columns={columns}
        rows={rowsCount}
        gridSelection={gridSelection}
        onGridSelectionChange={handleGridSelectionChange}
        highlightRegions={highlightRegions}
        headerHeight={styles.headerHeight}
        rowHeight={styles.rowHeight}
        showSearch={showSearch}
        searchValue={searchValue}
        searchResults={searchResults}
        onSearchValueChange={handleSearchValueChange}
        onSearchResultsChanged={handleSearchResultsChanged}
        onSearchClose={handleSearchClose}
        onColumnResize={onColumnResize}
        onHeaderMenuClick={onHeaderMenuClick}
        customRenderers={customRenderers}
        {...options}
        theme={{
          // Use styles from config
          accentColor: styles.accentColor,
          accentFg: styles.accentFg,
          accentLight: styles.accentLight,
          textDark: styles.textDark,
          textMedium: styles.textMedium,
          textLight: styles.textLight,
          textBubble: styles.textBubble,
          bgIconHeader: styles.bgIconHeader,
          fgIconHeader: styles.fgIconHeader,
          textHeader: styles.textHeader,
          textGroupHeader: styles.textGroupHeader,
          textHeaderSelected: styles.textHeaderSelected,
          bgCell: styles.bgCell,
          bgCellMedium: styles.bgCellMedium,
          bgHeader: styles.bgHeader,
          bgHeaderHasFocus: styles.bgHeaderHasFocus,
          bgHeaderHovered: styles.bgHeaderHovered,
          bgBubble: styles.bgBubble,
          bgBubbleSelected: styles.bgBubbleSelected,
          bgSearchResult: styles.bgSearchResult,
          borderColor: styles.borderColor,
          horizontalBorderColor: styles.horizontalBorderColor,
          drilldownBorder: styles.drilldownBorder,
          linkColor: styles.linkColor,
          cellHorizontalPadding: styles.cellHorizontalPadding,
          cellVerticalPadding: styles.cellVerticalPadding,
          headerFontStyle: styles.headerFontStyle,
          baseFontStyle: styles.baseFontStyle,
          fontFamily: styles.fontFamily,
          editorFontSize: styles.editorFontSize,
          lineHeight: styles.lineHeight,
        }}
      />
      <DataDetailsDrawer
        drawerWidth={"30vw"}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        data={selectedTrip}
        dataDrawerBg={props.filterDrawerBg}
        dataDrawerTextColor={props.filterdropdownTextColor}
        dataDrawerBoxBg={props.filterdropdownBg}
      />
      <CustomModal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      data={modalData}
      ></CustomModal>
      {renderColumnMenu()}
    </div>
  );
};

TableComponent.propTypes = {
  data: PropTypes.array.isRequired,
  config: PropTypes.object.isRequired,
};

export default TableComponent;
